import { useEffect, useRef } from 'react';
import { cn } from '@shared/lib';
import { HERO_LOOK, type CharLook } from './looks';
import { buildStatic, drawBus, drawCab, drawDecor, drawTrain, type StaticLayer } from './render';
import { bubbleSprite, charSprite, markerSprite, type Dir, type MarkerIcon } from './sprites';
import { BUILDINGS, buildWalkable, DECOR, findPath, MAP_H, MAP_W, PLACES, placeAtTile, TILE } from './world';

/**
 * Пиксельный Лондон на canvas: облегчённая проходилка (решение владельца 2026-10-22) — герой идёт к месту по
 * нажатию или когда история меняет место, свободной ходьбы и столкновений нет. Масштаб целый в пикселях
 * устройства, чтобы пиксели не мылились; камера ведёт героя, карту можно двигать пальцем по горизонтали.
 */

export interface WorldActor {
  id: string;
  look: CharLook;
  place: string;
  talking?: boolean;
}

export interface WorldMarker {
  place: string;
  icon: MarkerIcon;
}

export interface PixelWorldProps {
  mode: 'day' | 'night';
  heroPlace: string;
  actors?: WorldActor[];
  markers?: WorldMarker[];
  rain?: boolean;
  train?: boolean;
  /** Герой говорит по телефону: стоит лицом к нам с трубкой. */
  phone?: boolean;
  traffic?: boolean;
  /** Где по высоте держать героя (0 — верх, 1 — низ): под окном диалога — повыше. */
  focusY?: number;
  /** Сколько клеток примерно видно по ширине. */
  zoom?: 'near' | 'far';
  /** Не больше стольких клеток по высоте: на портретном экране во всю высоту иначе видна вся карта и камере некуда
   *  двигаться — герой уходит под окно диалога («сцена на весь экран», 2026-10-27). */
  rows?: number;
  onTapPlace?: (place: string) => void;
  onArrive?: (place: string) => void;
  ariaLabel: string;
  className?: string;
}

let shared: { layer: StaticLayer; walk: boolean[][] } | null = null;
function sharedWorld() {
  if (!shared) {
    const layer = buildStatic();
    shared = { layer, walk: buildWalkable(layer.ground) };
  }
  return shared;
}

const BLOCKING_DECOR = DECOR.filter((d) => d.kind !== 'flowers' && d.kind !== 'bench');
const LAMPS = DECOR.filter((d) => d.kind === 'lamp');

interface Drop {
  x: number;
  y: number;
  len: number;
  v: number;
}

export function PixelWorld(props: PixelWorldProps) {
  const { heroPlace, ariaLabel, className } = props;
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const propsRef = useRef(props);
  propsRef.current = props;
  const start = PLACES[heroPlace]?.hero ?? PLACES.start.hero;
  const sim = useRef({
    hx: start[0],
    hy: start[1],
    dir: 'up' as Dir,
    path: [] as [number, number][],
    target: heroPlace,
    walked: 0,
    camX: 0,
    camY: 0,
    camReady: false,
    manual: false,
    view: { w: 0, h: 0, scale: 1, dpr: 1 },
    drag: null as null | { x: number; y: number; camX: number; camY: number; moved: boolean },
    drops: [] as Drop[],
    t: 0,
    reduced: false,
  });

  // Смена места: герой идёт по кратчайшему пути (при «меньше движения» — сразу на месте).
  useEffect(() => {
    const s = sim.current;
    const spot = PLACES[heroPlace]?.hero;
    if (!spot) return;
    s.target = heroPlace;
    s.manual = false;
    const from: [number, number] = [Math.round(s.hx), Math.round(s.hy)];
    const path = s.reduced ? [] : findPath(sharedWorld().walk, from, spot);
    if (!path.length) {
      s.hx = spot[0];
      s.hy = spot[1];
      s.path = [];
      s.dir = 'up';
      propsRef.current.onArrive?.(heroPlace);
    } else {
      s.path = path;
    }
  }, [heroPlace]);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const s = sim.current;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    s.reduced = motion.matches;
    const { layer } = sharedWorld();
    const mapWpx = MAP_W * TILE;
    const mapHpx = MAP_H * TILE;

    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      if (rect.width < 2 || rect.height < 2) return;
      const dpr = window.devicePixelRatio || 1;
      const far = propsRef.current.zoom === 'far';
      const tiles = far ? (rect.width < 560 ? 17 : 28) : rect.width < 560 ? 11 : 17;
      const rows = propsRef.current.rows;
      const byWidth = Math.max(1, Math.floor((rect.width * dpr) / (TILE * tiles)));
      const scale = rows ? Math.max(byWidth, Math.ceil((rect.height * dpr) / (TILE * rows))) : byWidth;
      const w = Math.ceil((rect.width * dpr) / scale);
      const h = Math.ceil((rect.height * dpr) / scale);
      canvas.width = w;
      canvas.height = h;
      canvas.style.width = `${(w * scale) / dpr}px`;
      canvas.style.height = `${(h * scale) / dpr}px`;
      s.view = { w, h, scale, dpr };
      s.camReady = false;
      s.drops = Array.from({ length: Math.round((w * h) / 900) }, () => ({
        x: Math.random() * (w + 40),
        y: Math.random() * h,
        len: 3 + Math.random() * 3,
        v: 150 + Math.random() * 90,
      }));
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);

    const clampCam = (x: number, y: number): [number, number] => {
      const { w, h } = s.view;
      // Сцена на весь экран: низ закрыт панелями — камере можно уйти ниже края карты (там река, дальше — фон того же
      // цвета), иначе места у реки (закусочная, мост) не встают в просвет над окном диалога.
      const stage = !!propsRef.current.rows;
      const cx = w >= mapWpx ? (mapWpx - w) / 2 : Math.max(0, Math.min(mapWpx - w, x));
      const cy = h >= mapHpx && !stage ? (mapHpx - h) / 2 : Math.max(0, Math.min(stage ? mapHpx - h * 0.4 : mapHpx - h, y));
      return [cx, cy];
    };

    const update = (dt: number) => {
      const p = propsRef.current;
      s.t += dt;
      if (s.path.length) {
        const speed = Math.max(8, s.path.length / 1.4);
        let step = speed * dt;
        while (step > 0 && s.path.length) {
          const [nx, ny] = s.path[0];
          const dx = nx - s.hx;
          const dy = ny - s.hy;
          const dist = Math.hypot(dx, dy);
          if (Math.abs(dx) > Math.abs(dy)) s.dir = dx > 0 ? 'right' : 'left';
          else if (dy !== 0) s.dir = dy > 0 ? 'down' : 'up';
          if (dist <= step) {
            s.hx = nx;
            s.hy = ny;
            step -= dist;
            s.walked += dist;
            s.path.shift();
          } else {
            s.hx += (dx / dist) * step;
            s.hy += (dy / dist) * step;
            s.walked += step;
            step = 0;
          }
        }
        if (!s.path.length) {
          s.dir = 'up';
          p.onArrive?.(s.target);
        }
      }
      const { w, h } = s.view;
      if (!s.manual || s.path.length) {
        const fy = p.focusY ?? 0.5;
        const [tx, ty] = clampCam(s.hx * TILE + 8 - w / 2, s.hy * TILE + 8 - h * fy);
        if (!s.camReady || s.reduced) {
          s.camX = tx;
          s.camY = ty;
          s.camReady = true;
        } else {
          const k = 1 - Math.exp(-dt * 5);
          s.camX += (tx - s.camX) * k;
          s.camY += (ty - s.camY) * k;
        }
      }
      if (p.rain && !s.reduced) {
        for (const d of s.drops) {
          d.y += d.v * dt;
          d.x -= d.v * dt * 0.25;
          if (d.y > h) {
            d.y = -d.len;
            d.x = Math.random() * (w + 40);
          }
        }
      }
    };

    const draw = () => {
      const p = propsRef.current;
      const { w, h } = s.view;
      const night = p.mode === 'night';
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.imageSmoothingEnabled = false;
      ctx.fillStyle = night ? '#0d1226' : '#3f86d9';
      ctx.fillRect(0, 0, w, h);
      const camX = Math.round(s.camX);
      const camY = Math.round(s.camY);
      ctx.translate(-camX, -camY);
      ctx.drawImage(layer.canvas, 0, 0);

      // блики на воде
      for (let i = 0; i < 40; i++) {
        const sx = (i * 977 + Math.floor(s.t * 8) * (i % 3 === 0 ? 1 : -1) * 3) % mapWpx;
        const sy = 24 * TILE + 5 + ((i * 53) % ((MAP_H - 24) * TILE - 8));
        if (Math.floor(s.t * 2 + i) % 3 === 0) {
          ctx.fillStyle = night ? 'rgba(200, 220, 255, 0.55)' : 'rgba(255, 255, 255, 0.7)';
          ctx.fillRect((sx + mapWpx) % mapWpx, sy, 2, 1);
        }
      }

      // предметы и персонажи по y
      type Item = { y: number; draw: () => void };
      const items: Item[] = BLOCKING_DECOR.map((d) => ({ y: d.y * TILE + 15, draw: () => drawDecor(ctx, d, night) }));
      const bob = (seed: number) => (s.reduced ? 0 : Math.floor(s.t * 1.6 + seed) % 2);
      const actors = p.actors ?? [];
      actors.forEach((a, i) => {
        const place = PLACES[a.place];
        if (!place) return;
        const [ax, ay] = place.npc;
        items.push({
          y: ay * TILE + 15,
          draw: () => {
            ctx.fillStyle = 'rgba(20, 24, 40, 0.3)';
            ctx.fillRect(ax * TILE + 3, ay * TILE + 14, 10, 2);
            ctx.drawImage(charSprite(a.look, 'down', 0), ax * TILE, ay * TILE - bob(i));
            if (a.talking) {
              const by = ay * TILE - 12 - (s.reduced ? 0 : Math.round(Math.sin(s.t * 5) * 1.5));
              ctx.drawImage(bubbleSprite(), ax * TILE + 2, by);
            }
          },
        });
      });
      const heroX = s.hx * TILE;
      const heroY = s.hy * TILE;
      const moving = s.path.length > 0;
      const frame = (moving ? [1, 0, 2, 0][Math.floor(s.walked * 4) % 4] : 0) as 0 | 1 | 2;
      items.push({
        y: heroY + 15,
        draw: () => {
          ctx.fillStyle = 'rgba(20, 24, 40, 0.3)';
          ctx.fillRect(Math.round(heroX) + 3, Math.round(heroY) + 14, 10, 2);
          const dir: Dir = p.phone && !moving ? 'down' : s.dir;
          ctx.drawImage(charSprite(HERO_LOOK, dir, frame), Math.round(heroX), Math.round(heroY));
          if (p.phone && !moving) {
            ctx.fillStyle = '#1b1a26';
            ctx.fillRect(Math.round(heroX) + 12, Math.round(heroY) + 3, 3, 6);
            ctx.fillStyle = '#9fd0f5';
            ctx.fillRect(Math.round(heroX) + 13, Math.round(heroY) + 4, 1, 3);
          }
        },
      });
      if (p.traffic) {
        const span = mapWpx + 120;
        const busX = ((s.t * 34) % span) - 60;
        const cabX = mapWpx + 60 - ((s.t * 52 + 300) % span);
        items.push({ y: 10 * TILE + 13, draw: () => drawBus(ctx, busX, 10 * TILE + 1, false) });
        items.push({ y: 11 * TILE + 13, draw: () => drawCab(ctx, cabX, 11 * TILE + 1, true) });
      } else if (night) {
        items.push({ y: 11 * TILE + 13, draw: () => drawBus(ctx, 19 * TILE - 8, 11 * TILE + 1, false) });
      }
      if (p.train) items.push({ y: 22 * TILE + 12, draw: () => drawTrain(ctx, 3 * TILE, 22 * TILE + 3) });
      items.sort((a, b) => a.y - b.y).forEach((it) => it.draw());

      // значки над домами
      for (const m of p.markers ?? []) {
        const b = BUILDINGS.find((x) => x.id === m.place);
        const place = PLACES[m.place];
        const mx = b ? b.x * TILE + (b.w * TILE) / 2 - 6 : place ? place.npc[0] * TILE + 2 : null;
        const my = b ? (b.y > 0 ? b.y * TILE - 16 : b.y * TILE + 6) : place ? place.npc[1] * TILE - 18 : null;
        if (mx === null || my === null) continue;
        const lift = s.reduced ? 0 : Math.round(Math.abs(Math.sin(s.t * 3 + mx)) * 3);
        ctx.drawImage(markerSprite(m.icon), Math.round(mx), my - lift);
      }

      if (night) {
        ctx.fillStyle = 'rgba(10, 14, 42, 0.56)';
        ctx.fillRect(camX, camY, w, h);
        ctx.globalCompositeOperation = 'lighter';
        const glow = (x: number, y: number, r: number, color: string) => {
          if (x < camX - r || x > camX + w + r || y < camY - r || y > camY + h + r) return;
          const g = ctx.createRadialGradient(x, y, 0, x, y, r);
          g.addColorStop(0, color);
          g.addColorStop(1, 'rgba(0, 0, 0, 0)');
          ctx.fillStyle = g;
          ctx.fillRect(x - r, y - r, r * 2, r * 2);
        };
        for (const l of LAMPS) glow(l.x * TILE + 8, l.y * TILE - 6, 30, 'rgba(255, 200, 110, 0.32)');
        glow(heroX + 8, heroY + 8, 34, 'rgba(255, 220, 170, 0.22)');
        for (const a of actors) {
          const place = PLACES[a.place];
          if (place && a.talking) glow(place.npc[0] * TILE + 8, place.npc[1] * TILE + 8, 30, 'rgba(255, 220, 170, 0.2)');
        }
        ctx.globalCompositeOperation = 'source-over';
        for (let i = 0; i < layer.windows.length; i++) {
          const win = layer.windows[i];
          if (win.x > camX + w || win.x + win.w < camX || win.y > camY + h || win.y + win.h < camY) continue;
          const flicker = !s.reduced && i % 17 === 0 && Math.floor(s.t * 0.7 + i) % 5 === 0;
          if (!win.warm || flicker) continue;
          ctx.fillStyle = win.w <= 4 ? '#b48cff' : '#ffd27a';
          ctx.fillRect(win.x, win.y, win.w, win.h);
          ctx.fillStyle = 'rgba(255, 210, 122, 0.18)';
          ctx.fillRect(win.x - 1, win.y + win.h, win.w + 2, 2);
        }
      }

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      if (p.rain) {
        ctx.fillStyle = night ? 'rgba(170, 200, 255, 0.5)' : 'rgba(220, 235, 255, 0.6)';
        for (const d of s.drops) {
          for (let k = 0; k < d.len; k++) ctx.fillRect(Math.round(d.x - k * 0.25), Math.round(d.y - k), 1, 1);
        }
      }
    };

    let raf = 0;
    let last = performance.now();
    let visible = true;
    const loop = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (visible && !document.hidden) {
        update(dt);
        draw();
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    const io = new IntersectionObserver(([entry]) => {
      visible = entry?.isIntersecting ?? true;
    });
    io.observe(wrap);

    // нажатие — выбор дома; перетаскивание по горизонтали — сдвиг карты
    const onDown = (e: PointerEvent) => {
      s.drag = { x: e.clientX, y: e.clientY, camX: s.camX, camY: s.camY, moved: false };
    };
    const onMove = (e: PointerEvent) => {
      const d = s.drag;
      if (!d) return;
      const dx = e.clientX - d.x;
      const dy = e.clientY - d.y;
      if (!d.moved && Math.hypot(dx, dy) < 6) return;
      d.moved = true;
      const k = s.view.dpr / s.view.scale;
      [s.camX, s.camY] = clampCam(d.camX - dx * k, d.camY - dy * k);
      s.manual = true;
    };
    const onUp = (e: PointerEvent) => {
      const d = s.drag;
      s.drag = null;
      if (!d || d.moved) return;
      const rect = canvas.getBoundingClientRect();
      const k = s.view.dpr / s.view.scale;
      const mx = (e.clientX - rect.left) * k + s.camX;
      const my = (e.clientY - rect.top) * k + s.camY;
      const place = placeAtTile(Math.floor(mx / TILE), Math.floor(my / TILE));
      if (place) propsRef.current.onTapPlace?.(place);
    };
    const onCancel = () => {
      s.drag = null;
    };
    canvas.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onCancel);
    const onMotion = () => {
      s.reduced = motion.matches;
    };
    motion.addEventListener('change', onMotion);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      canvas.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onCancel);
      motion.removeEventListener('change', onMotion);
    };
  }, []);

  return (
    <div ref={wrapRef} className={cn('relative overflow-hidden', className)}>
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={ariaLabel}
        className="block select-none [image-rendering:pixelated]"
        style={{ touchAction: 'pan-y' }}
      />
    </div>
  );
}
